import * as RadixPopover from '@radix-ui/react-popover';
import classNames from 'classnames';
import { isUndefined } from 'lodash';
import { memo, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { usePrevious } from 'react-use';

import { type SearchBoardsResponse, type SearchSavedFiltersResponse, type SuggestionsResponse } from '@air/api/types';
import { Command } from '@air/cmdk';
import { Close } from '@air/next-icons';
import { IconButton } from '@air/primitive-icon-button';

import { SearchCommandActions } from '~/components/SearchCommand/SearchCommandActions';
import {
  SearchCommandClearButton,
  type SearchCommandClearButtonProps,
} from '~/components/SearchCommand/SearchCommandClearButton';
import { SearchCommandInputBase } from '~/components/SearchCommand/SearchCommandInputBase';
import { SearchCommandList } from '~/components/SearchCommand/SearchCommandList';
import { SearchCommandTrigger } from '~/components/SearchCommand/SearchCommandTrigger';
import { SmartSearchCommandList } from '~/components/SearchCommand/SmartSearchCommandList';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';
import { setHasSeenSmartSearchPromotion } from '~/utils/SearchUtils';

const SEARCH_INPUT_FOCUS_DELAY_MS = 50;

export type SearchCommandProps = {
  compact?: boolean;
  defaultSearch?: string;
  filterBar?: ReactNode;
  filtersCount?: number;
  hideSuggestions?: boolean;
  onBoardClick?: (board: SearchBoardsResponse) => void;
  onOpenChange: (isOpen: boolean) => void;
  onSavedFilterSelect?: (savedFilter: SearchSavedFiltersResponse) => void;
  onSearchChange?: (search: string) => void;
  onSuggestionClick?: (suggestion: string) => void;
  placeholder?: string;
  suggestions?: SuggestionsResponse;
  onClear?: SearchCommandClearButtonProps['onClick'];
  renderActions?: ({ onClearSearchValue }: { onClearSearchValue: () => void }) => ReactNode;
  renderCustomContent?: () => ReactNode;
  search?: string;
  onSimilarImageSearch?: (base64Image: string) => void;
};

export const SearchCommand = memo(
  ({
    compact,
    filterBar,
    filtersCount,
    hideSuggestions = false,
    onBoardClick: _onBoardClick,
    onClear,
    onOpenChange,
    onSavedFilterSelect: _onSavedFilterSelect,
    onSearchChange,
    onSuggestionClick: _onSuggestionClick,
    onSimilarImageSearch: _onSimilarImageSearch,
    placeholder,
    renderActions,
    renderCustomContent,
    search,
    suggestions,
  }: SearchCommandProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
    const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
    const rightPanelOpen = useWorkspaceRightPanelOpen();
    const { searchType, canUseSmartSearch } = usePrivateSearchContext();

    const isSplitScreen = generativeCanvasEnabled && rightPanelOpen;

    const prevSearchType = usePrevious(searchType);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      // We want to make sure that the value is set to an empty string instead of undefined
      // to avoid the input defaulting on the `defaultValue` prop.
      setSearchValue(search ?? '');
    }, [search]);

    useEffect(() => {
      onOpenChange(isOpen);
    }, [isOpen, onOpenChange]);

    const onOpen = useCallback(() => {
      setIsOpen(true);
    }, []);

    useEffect(() => {
      if (canUseSmartSearch && !isUndefined(prevSearchType) && prevSearchType !== searchType) {
        if (isUndefined(search) && searchType === 'keyword') {
          setIsOpen(true);
        }

        // NOTE: Workaround for race condition when switching between Popover trigger and search input.
        // The search input ref needs time to become available after the state change before we can
        // call blur() or focus() on it, so we add a 50ms delay to ensure the ref is ready.
        window.setTimeout(() => {
          if (search) {
            searchInputRef.current?.blur();
          } else {
            searchInputRef.current?.focus();
          }
        }, SEARCH_INPUT_FOCUS_DELAY_MS);
      }
    }, [canUseSmartSearch, prevSearchType, searchType, search]);

    const onBoardClick = useCallback(
      (board: SearchBoardsResponse) => {
        setIsOpen(false);
        _onBoardClick?.(board);
      },
      [_onBoardClick],
    );

    const onSavedFilterSelect = useCallback(
      (savedFilter: SearchSavedFiltersResponse) => {
        setIsOpen(false);
        _onSavedFilterSelect?.(savedFilter);
      },
      [_onSavedFilterSelect],
    );

    const onSuggestionClick = useCallback(
      (suggestion: string) => {
        setIsOpen(false);
        setSearchValue(suggestion);
        _onSuggestionClick?.(suggestion);
      },
      [_onSuggestionClick],
    );

    const onSimilarImageSearch = useCallback(
      (base64Image: string) => {
        setIsOpen(false);
        setSearchValue('');
        _onSimilarImageSearch?.(base64Image);
      },
      [_onSimilarImageSearch],
    );

    const onValueChange = useCallback(
      (value: string) => {
        setSearchValue(value);
        onSearchChange?.(value);
        if (canUseSmartSearch) {
          setHasSeenSmartSearchPromotion(true);
        }
      },
      [onSearchChange, canUseSmartSearch],
    );

    const onClearSearchValue = useCallback(() => {
      setSearchValue('');
      onSearchChange?.('');
    }, [onSearchChange]);

    useHotkeys('meta+p,ctrl+p', onOpen, {
      enableOnFormTags: true,
      preventDefault: true,
    });

    return (
      <Command className="w-full" data-testid="SEARCH_COMMAND" shouldFilter={false}>
        <RadixPopover.Root open={isOpen} onOpenChange={setIsOpen}>
          <div className="relative">
            <RadixPopover.Trigger asChild>
              <SearchCommandTrigger
                compact={compact}
                count={filtersCount}
                onClick={onOpen}
                placeholder={placeholder}
                search={searchValue}
              />
            </RadixPopover.Trigger>
            {!!renderActions && <SearchCommandActions>{renderActions({ onClearSearchValue })}</SearchCommandActions>}
            {!!onClear && !!searchValue && <SearchCommandClearButton onClick={onClear} />}
          </div>

          <RadixPopover.Portal>
            <RadixPopover.Content
              className={classNames(
                'data-[state=closed]:fade-out data-[state=open]:fade-in relative top-[calc(var(--radix-popover-trigger-height)*-1)] left-0 flex max-h-[calc(var(--radix-popover-content-available-height)-32px)] w-[var(--radix-popover-trigger-width)] flex-col transition-all data-[state=closed]:animate-out data-[state=open]:animate-in',
                'gap-1 rounded-8 bg-grey-transparent',
              )}
              data-testid="SEARCH_COMMAND_CONTENT"
            >
              {/* Search Input Section */}
              <div
                className={classNames(
                  'flex w-full shrink-0 flex-col',
                  'overflow-hidden rounded-8 border border-grey-6 bg-grey-1 shadow-search-command',
                )}
              >
                <div className="relative">
                  <SearchCommandInputBase
                    compact={compact}
                    count={filtersCount}
                    onValueChange={onValueChange}
                    placeholder={placeholder}
                    value={searchValue}
                    canUseSmartSearch={canUseSmartSearch}
                    ref={searchInputRef}
                  />
                  {!!renderActions && (
                    <SearchCommandActions>{renderActions({ onClearSearchValue })}</SearchCommandActions>
                  )}
                  {!!onClear && <SearchCommandClearButton onClick={onClear} />}
                  {isSplitScreen && isOpen && !onClear && (
                    <IconButton
                      appearance="ghost"
                      color="grey"
                      className="absolute top-1/2 right-2 z-20 -translate-y-1/2"
                      icon={Close}
                      label="Close search"
                      onClick={() => {
                        onClearSearchValue();
                        setIsOpen(false);
                      }}
                    />
                  )}
                </div>
                {searchType === 'keyword' && filterBar}
              </div>

              {/* Search Results Section */}
              {searchType === 'keyword' && (
                <SearchCommandList
                  isOpen={isOpen}
                  hideSuggestions={hideSuggestions}
                  searchValue={searchValue}
                  suggestions={suggestions}
                  renderCustomContent={renderCustomContent}
                  onSuggestionClick={onSuggestionClick}
                  onSavedFilterSelect={onSavedFilterSelect}
                  onBoardClick={onBoardClick}
                  onSimilarImageSearch={onSimilarImageSearch}
                />
              )}
              {searchType === 'smart' && (
                <SmartSearchCommandList
                  isOpen={isOpen}
                  onSelect={onSuggestionClick}
                  onSimilarImageSearch={onSimilarImageSearch}
                />
              )}
            </RadixPopover.Content>
          </RadixPopover.Portal>
        </RadixPopover.Root>
      </Command>
    );
  },
);

SearchCommand.displayName = 'SearchCommand';
